import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useAuth from 'hooks/useAuth';
import { api } from 'controllers/network/apiClient';

import type {
  IMarkStepCompleted,
  OnboardingState,
} from 'providers/ProgressiveOnboarding/types';

export const ProgressiveOnboardingQueryKeys = {
  getOnboardingStates: () =>
    ['progressive-onboarding', 'onboarding-states'] as const,
};

const getOnboardingStates = async () => {
  const result = await api.get<{ onboardingStates: OnboardingState[] }>(
    '/v2/progressive_onboarding/states',
  );

  return result.onboardingStates;
};

/** Do not call this hook directly! Only interact via useProgressiveOnboarding hook. */
export const useGetOnboardingStates = () => {
  const { isLoggedIn } = useAuth();

  return useQuery({
    queryKey: ProgressiveOnboardingQueryKeys.getOnboardingStates(),
    queryFn: getOnboardingStates,

    enabled: isLoggedIn,
  });
};

const markStepCompleted = async ({
  guideKey,
  stepKey,
  metadata,
}: IMarkStepCompleted) => {
  return api.post(
    `/v2/progressive_onboarding/features/${guideKey}/steps/${stepKey}/complete`,
    { meta_data: metadata },
  );
};

export const useMarkStepCompleted = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['progressive-onboarding', 'mark-step-completed'],
    mutationFn: markStepCompleted,
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ProgressiveOnboardingQueryKeys.getOnboardingStates(),
      });
    },
  });
};

/**
 * Returns a helper function to invlaidate the queryCache and get the latest onboarding state from the server.
 * Used in various parts of the app after performing some action that may change the onboarding state.
 * eg: After setting up the Brand Center
 */
export const useInvalidateOnboardingState = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: ProgressiveOnboardingQueryKeys.getOnboardingStates(),
    });
};
